import React, { useEffect, useState, useMemo } from "react";
import { sunboundPayProxy } from '@proxies';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSpinnerContext } from '@contexts';
import { Constants, helpers, Enums } from "@shared";
import { useFormSubmission } from "@hooks";
import { Resident } from "@common";

const DefaultPaymentMethod = () => {

    const navigate = useNavigate(), location = useLocation();
    const { setSpinnerShow } = useSpinnerContext();
    const [payabli, setPayabli] = useState(null);
    const [pmReady, setPMReady] = useState(false);

    const { setFormState, clearFormState, hasFormError } = useFormSubmission();
    const {
        state: {
            selectedPaymentType,
            account,
            setupAccounts,
            hasMultipleAccounts
        }
    } = location;
    const {
        paypoint,
        idPayabli,
        idAccount,
        residentName
    } = account;


    const payabliConfig = {
        type: `methodEmbedded`,
        rootContainer: `payabli`,
        defaultOpen: selectedPaymentType === Enums.PaymentType.ACH ? `ach` : `card`,
        token: window.settings.SUNBOUNDPAY_PAYABLI_PUBLIC_TOKEN,
        temporaryToken: false,
        entryPoint: paypoint,
        card: {
            enabled: true,
            amex: true,
            discover: true,
            visa: true,
            mastercard: true,
            inputs: {
                cardHolderName: {
                    label: `Name on Card`,
                    placeholder: `Name on card`,
                    floating: false,
                    value: ``,
                    size: 12,
                    row: 0,
                    order: 0
                },
                cardNumber: {
                    label: `Card Number`,
                    placeholder: `1234 1234 1234 1234`,
                    floating: false,
                    size: 6,
                    row: 1,
                    order: 0
                },
                cardExpirationDate: {
                    label: `Expiration Date`,
                    placeholder: `MM/YY`,
                    floating: false,
                    size: 6,
                    row: 1,
                    order: 1
                },
                cardCvv: {
                    label: `CVV/CVC`,
                    placeholder: `CVV/CVC`,
                    floating: false,
                    size: 6,
                    row: 2,
                    order: 0
                },
                cardZipcode: {
                    label: `ZIP/Postal Code`,
                    placeholder: `Zip/Postal Code`,
                    floating: false,
                    size: 6,
                    row: 2,
                    order: 1
                }
            }
        },
        ach: {
            enabled: true,
            checking: true,
            savings: true,
            inputs: {
                achAccount: {
                    confirm: true
                },
                achRouting: {
                    confirm: true
                }
            }
        },
        customerData: {
            customerId: idPayabli
        },
        functionCallBackSuccess: function (response) {
            window.postMessage({ message: `callSucceeded`, response });
        },
        functionCallBackReady: function (status) {
            window.postMessage({ message: `payabliReady`, status });
        },
        functionCallBackError: function (errors) {
            console.log(`functionCallBackError`, errors);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const callSucceeded = async (data) => {

        const { response: { isSuccess, responseData: { methodReferenceId: token, resultCode, resultText } } } = data;

        if (isSuccess === true) {
            try {
                const dpm = await sunboundPayProxy.addPaymentMethod(idAccount, token, true);

                if (hasMultipleAccounts === true) {
                    if (setupAccounts.length > 0) {
                        const [nextAccount, ...rest] = setupAccounts;
                        helpers.navigateTo(`/existing-payment-methods`, location, navigate, undefined, { account: nextAccount, setupAccounts: [...rest] });
                    } else {
                        helpers.navigateNext(location, navigate, { missingAccounts: false });
                    }
                } else {
                    const { paymentPreferences } = account;
                    const newAccount = { ...account, paymentPreferences: { ...paymentPreferences, defaultPaymentMethod: dpm } };
                    helpers.navigateNext(location, navigate, undefined, { account: newAccount });
                }

            } catch (error) {
                console.log(error);
            } finally {
                setSpinnerShow(false);
            }
        } else {
            setSpinnerShow(false);
            console.log(`Error (${resultCode}): ${resultText}`);
            setFormState({ resultCode, resultText });
        }
    };

    const initRegistration = () => {
        setSpinnerShow(true);
        try {
            payabli.payabliExec(`method`);
        } catch (error) {
            console.log(error);
            setSpinnerShow(false);
        }

    };

    const onMessageReceivedFromIframe = React.useCallback(
        async (event) => {
            const { data } = event;
            if (data) {
                if (data.message === `payabliReady`) {
                    clearFormState();
                    const [, formReady] = data.status;
                    setPMReady(formReady);
                }
                if (data.message === `callSucceeded`) {
                    await callSucceeded(data);
                }
            }
        },
        [payabli]
    );

    useEffect(() => {
        setSpinnerShow(true);
        const component = new PayabliComponent(payabliConfig);
        setPayabli(component);
    }, []);

    const payabliLoaded = () => setSpinnerShow(false);

    useEffect(() => {
        window.addEventListener(`message`, onMessageReceivedFromIframe);
        if (payabli && payabli.payabliIframe.addEventListener)
            payabli.payabliIframe.addEventListener(`load`, payabliLoaded);

        return () => {
            window.removeEventListener(`message`, onMessageReceivedFromIframe);
            if (payabli && payabli.payabliIframe.removeEventListener)
                payabli.payabliIframe.removeEventListener(`load`, payabliLoaded);
        };
    }, [onMessageReceivedFromIframe]);

    const resident = useMemo(() => {
        if (hasMultipleAccounts === true) {
            return (<div className="w-10/12 self-center"><Resident residentName={residentName} /></div>);
        }
        return null;
    }, []);

    return (
        <div className="page-content-wrapper">
            <h1 className="page-header">Add payment method</h1>
            <span className="page-subheader">We will use this for online payments</span>
            {resident}
            <div id="payabli" className="w-full"></div>
            <div className="page-action mt-3">
                <button type="button" className="btn btn-primary" disabled={!pmReady} onClick={initRegistration}>Register</button>
                {
                    hasFormError &&
                    <p className="text-red-600 mt-3">
                        There was an issue registering your payment method. Please verify your payment information and try again. For more details, see our <a className="underline" href={Constants.Help.FAQErrors} target="_blank" rel="noreferrer">FAQ</a>.
                    </p>
                }
            </div>
        </div>
    );
};

export default DefaultPaymentMethod;
